/**
 * AI Rigging / Project Rig Memory feature gates.
 *
 * Authoritative source (production): GET /api/v1/execution-providers/ → payload.ai_rigging
 * (loaded at session bootstrap via AuthContext). Django env vars remain backend-only.
 *
 * Build-time VITE_* values are fallbacks only for tests / offline local Vite.
 * Fail closed when neither server nor env enables a flag. Do not rely on VITE_*
 * feature flags in production deploys.
 */

/** @typedef {{
 *   ai_rigging_enabled: boolean,
 *   project_rig_memory_enabled: boolean,
 *   job_creation_enabled: boolean,
 *   worker_runtime_enabled: boolean,
 *   autoskin_enabled: boolean,
 *   export_enabled: boolean,
 *   reggie_assistant_enabled?: boolean,
 * }} AiRiggingServerFlags */

/** @type {AiRiggingServerFlags | null} */
let _serverFlags = null;
let _serverFlagsLoaded = false;

/**
 * @param {{ ai_rigging?: AiRiggingServerFlags } | AiRiggingServerFlags | null} payload
 */
export function setServerAiRiggingFlags(payload) {
  if (!payload) {
    _serverFlags = null;
    _serverFlagsLoaded = true;
    return;
  }
  const block = payload.ai_rigging || payload;
  if (!block || typeof block !== 'object') {
    _serverFlags = null;
    _serverFlagsLoaded = true;
    return;
  }
  _serverFlags = {
    ai_rigging_enabled: Boolean(block.ai_rigging_enabled),
    project_rig_memory_enabled: Boolean(block.project_rig_memory_enabled),
    job_creation_enabled: Boolean(block.job_creation_enabled),
    worker_runtime_enabled: Boolean(block.worker_runtime_enabled),
    autoskin_enabled: Boolean(block.autoskin_enabled),
    export_enabled: Boolean(block.export_enabled),
    reggie_assistant_enabled: Boolean(block.reggie_assistant_enabled),
  };
  _serverFlagsLoaded = true;
}

export function clearServerAiRiggingFlags() {
  _serverFlags = null;
  _serverFlagsLoaded = false;
}

export function aiRiggingServerFlagsLoaded() {
  return _serverFlagsLoaded;
}

export function getServerAiRiggingFlags() {
  return _serverFlags;
}

function envFlag(name) {
  return String(import.meta.env[name] || '').toLowerCase() === 'true';
}

function resolveFlag(serverKey, envName) {
  if (_serverFlags) {
    return Boolean(_serverFlags[serverKey]);
  }
  return envFlag(envName);
}

export function isAiRiggingUiEnabled() {
  return resolveFlag('ai_rigging_enabled', 'VITE_AI_RIGGING_ENABLED');
}

export function isProjectRigMemoryEnabled() {
  if (_serverFlags) {
    return Boolean(_serverFlags.project_rig_memory_enabled);
  }
  const raw = import.meta.env.VITE_PROJECT_RIG_MEMORY_ENABLED;
  if (raw === undefined || raw === '') {
    return isAiRiggingUiEnabled();
  }
  return String(raw).toLowerCase() === 'true';
}

export function isAiRiggingJobCreationEnabled() {
  return (
    isAiRiggingUiEnabled() &&
    isProjectRigMemoryEnabled() &&
    resolveFlag('job_creation_enabled', 'VITE_AI_RIGGING_JOB_CREATION_ENABLED')
  );
}

export function isAiRiggingWorkerRuntimeEnabled() {
  return resolveFlag(
    'worker_runtime_enabled',
    'VITE_AI_RIGGING_WORKER_RUNTIME_ENABLED'
  );
}

export function isAiRiggingAutoskinEnabled() {
  return resolveFlag('autoskin_enabled', 'VITE_AI_RIGGING_AUTOSKIN_ENABLED');
}

export function isAiRiggingExportEnabled() {
  return resolveFlag('export_enabled', 'VITE_AI_RIGGING_EXPORT_ENABLED');
}

/** @typedef {'configuration_hidden'|'memory_disabled'|'view_only'|'create_but_do_not_execute'|'executable'} RiggingRuntimeMode */

/**
 * @returns {{ mode: RiggingRuntimeMode, jobCreationEnabled: boolean, workerRuntimeEnabled: boolean, autoskinEnabled: boolean, exportEnabled: boolean }}
 */
export function getClientRuntimeAvailability() {
  if (!isAiRiggingUiEnabled()) {
    return {
      mode: 'configuration_hidden',
      jobCreationEnabled: false,
      workerRuntimeEnabled: false,
      autoskinEnabled: false,
      exportEnabled: false,
    };
  }
  if (!isProjectRigMemoryEnabled()) {
    return {
      mode: 'memory_disabled',
      jobCreationEnabled: false,
      workerRuntimeEnabled: false,
      autoskinEnabled: false,
      exportEnabled: false,
    };
  }
  const jobCreationEnabled = isAiRiggingJobCreationEnabled();
  const workerRuntimeEnabled = isAiRiggingWorkerRuntimeEnabled();
  let mode = 'view_only';
  if (jobCreationEnabled && workerRuntimeEnabled) mode = 'executable';
  else if (jobCreationEnabled) mode = 'create_but_do_not_execute';
  return {
    mode,
    jobCreationEnabled,
    workerRuntimeEnabled,
    autoskinEnabled: isAiRiggingAutoskinEnabled(),
    exportEnabled: isAiRiggingExportEnabled(),
  };
}

export const RIGGING_OPERATIONS = [
  {
    id: 'maya_project_setup',
    label: 'Project Setup',
    description:
      "Apply the project’s published Maya workspace, naming, directory, unit, axis, and compatibility policy.",
    resource: 'cpu',
    stages: ['project_setup', 'validation', 'artifacts', 'result_review'],
    requiredCapabilities: ['maya.project_setup', 'fabricatorstudio.runtime'],
    availability: () => ({ available: true, reason: '' }),
  },
  {
    id: 'maya_skeleton_build',
    label: 'Build Skeleton',
    description:
      'Create or update the character skeleton from the published guide and Skeleton Recipe.',
    resource: 'cpu',
    stages: ['skeleton', 'validation', 'artifacts', 'result_review'],
    requiredCapabilities: ['maya.skeleton_build', 'fabricatorstudio.runtime'],
    availability: () => ({ available: true, reason: '' }),
  },
  {
    id: 'maya_rig_build',
    label: 'Build Rig',
    description:
      'Construct the animation rig from the published Rig Recipe and trusted FabricatorStudio components.',
    resource: 'cpu',
    stages: ['rig', 'validation', 'artifacts', 'result_review'],
    requiredCapabilities: ['maya.rig_build', 'fabricatorstudio.runtime'],
    availability: () => ({ available: true, reason: '' }),
  },
  {
    id: 'maya_ai_autoskin',
    label: 'AI AutoSkin',
    description: 'Generate and validate skin weights using the published Skinning Profile.',
    resource: 'gpu',
    stages: ['autoskin', 'validation', 'artifacts', 'result_review'],
    requiredCapabilities: ['maya.ai_autoskin', 'fabricatorstudio.runtime'],
    availability: () =>
      isAiRiggingAutoskinEnabled()
        ? { available: true, reason: '' }
        : {
            available: false,
            reason: 'AI AutoSkin is not enabled in this environment.',
          },
  },
  {
    id: 'maya_engine_export',
    label: 'Engine Export',
    description: 'Generate an engine-ready export using a published Export Profile.',
    resource: 'cpu',
    stages: ['export', 'validation', 'artifacts', 'result_review'],
    requiredCapabilities: ['maya.engine_export', 'fabricatorstudio.runtime'],
    availability: () =>
      isAiRiggingExportEnabled()
        ? { available: true, reason: '' }
        : {
            available: false,
            reason: 'Engine export is not enabled in this environment.',
          },
  },
  {
    id: 'maya_character_pipeline',
    label: 'Complete Character Pipeline',
    description:
      'Run the configured character pipeline from setup through the enabled build, skinning, validation, and export stages.',
    resource: 'gpu',
    stages: [
      'project_setup',
      'skeleton',
      'rig',
      'autoskin',
      'validation',
      'export',
      'artifacts',
      'result_review',
    ],
    requiredCapabilities: ['maya.character_pipeline', 'fabricatorstudio.runtime'],
    availability: () =>
      isAiRiggingAutoskinEnabled() && isAiRiggingExportEnabled()
        ? { available: true, reason: '' }
        : {
            available: false,
            reason:
              'Complete Character Pipeline requires AutoSkin and export feature flags.',
          },
  },
];

export function getRiggingOperation(operationId) {
  return RIGGING_OPERATIONS.find((op) => op.id === operationId) || null;
}

export function operationLabel(operationId) {
  const op = getRiggingOperation(operationId);
  return op ? op.label : operationId || 'Unknown operation';
}

/**
 * Whether the Partner Console should expose project-level Rigging (configuration) navigation.
 * Requires Maya provider + AI Rigging UI enabled + Project Rig Memory enabled.
 */
export function shouldShowProjectRiggingNav({
  executionProvider,
  aiRiggingEnabled = isAiRiggingUiEnabled(),
  projectRigMemoryEnabled = isProjectRigMemoryEnabled(),
} = {}) {
  return (
    String(executionProvider || '').toLowerCase() === 'maya' &&
    Boolean(aiRiggingEnabled) &&
    Boolean(projectRigMemoryEnabled)
  );
}

/**
 * Whether the Partner Console should expose first-class Rigging Jobs (execution) navigation.
 * Parallel to Bake Jobs for Maya projects. Does not require worker runtime.
 */
export function shouldShowProjectRiggingJobsNav({
  executionProvider,
  aiRiggingEnabled = isAiRiggingUiEnabled(),
} = {}) {
  return (
    String(executionProvider || '').toLowerCase() === 'maya' && Boolean(aiRiggingEnabled)
  );
}
